import { type ReactNode, type RefObject, useRef } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import type { Guide } from './types';
import { StepLines } from './StepLines';
import { StepComments } from './StepComments';
import { getVideoEmbedUrl, getVideoSources } from './video';
import { useGuideVideoSync } from './useGuideVideoSync';

// The player keeps the wider column — it has a fixed 16:9 to fill — but not so
// much that the step text drops below a readable measure.
const LAYOUT_CLASS =
   'mx-auto grid max-w-[1180px] grid-cols-1 items-start gap-6 px-6 pb-6 pt-20 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]';

/**
 * A step of a chaptered video guide: the persistent player (YouTube iframe or
 * self-hosted <video>) plus the step's text. The player is mounted once for the
 * whole video run — this component stays at a stable position in the tree while
 * `stepIndex`/`step` change, so React never rebuilds the iframe (which would
 * reload the video). Parity with the video branch of embed.v5.steps.js.
 */
export function VideoStepScreen({
   guide,
   stepIndex,
   onVideoStep,
   footer,
}: {
   guide: Guide;
   stepIndex: number;
   onVideoStep: (stepNumber: number) => void;
   // The paginated app's pager; the full embed scrolls instead and passes none.
   footer?: ReactNode;
}) {
   const mediaRef = useRef<HTMLIFrameElement | HTMLVideoElement | null>(null);
   const { skipToCurrentStep } = useGuideVideoSync({ guide, mediaRef, stepIndex, onVideoStep });

   const step = guide.steps[stepIndex - 1];
   const isYoutube = guide.video_info?.type === 'youtube';
   const embedUrl = getVideoEmbedUrl(guide.video_info);
   const sources = getVideoSources(guide.video_info);

   return (
      <section className={LAYOUT_CLASS}>
         <div className="py-4">
            <div className="aspect-video max-h-[65vh] w-full overflow-hidden rounded-2xl bg-black [&_iframe]:size-full [&_iframe]:border-0 [&_video]:size-full">
               {isYoutube ? (
                  <iframe
                     ref={mediaRef as RefObject<HTMLIFrameElement>}
                     src={embedUrl}
                     title={step?.title || guide.title}
                     allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                     allowFullScreen
                  />
               ) : (
                  <video
                     ref={mediaRef as RefObject<HTMLVideoElement>}
                     muted
                     controls
                     loop={Boolean(App.videoLooping)}
                     preload="auto"
                  >
                     {sources.map(source => (
                        <source key={source.url} src={source.url} type={source.mime} />
                     ))}
                  </video>
               )}
            </div>
         </div>

         {/* md:pt-4 matches the video column's top padding so the skip button
             lines up with the video top instead of hugging the header. */}
         <div className="min-w-0 md:pt-4">
            <button
               type="button"
               onClick={skipToCurrentStep}
               className="button-outline button-sm mb-4 border-brand-500 bg-brand-50 text-brand-700 hover:bg-brand-100 hover:text-brand-700"
            >
               <FaIcon name="play" aria-hidden />
               {_js('Skip video to this step')}
            </button>
            <StepLines lines={step?.lines ?? []} />
         </div>

         {step && <StepComments step={step} className="col-span-full mt-6" />}

         {footer}
      </section>
   );
}
